import {
  extendVariants,
  Button as NextUIButton,
  ButtonProps as NextUIButtonProps,
} from "@heroui/react";
import { PressEvent } from "@react-types/shared";
import { TourElement, TrackEventProps } from "core/types";
import { generateTrackName } from "core/validationSchemas/utils";
import { Spinner } from "ds/ui";
import { colorVariants } from "ds/utils";
import React, { useMemo } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";

const ExtendedButton = extendVariants(NextUIButton, {
  compoundVariants: [
    // solid / color
    {
      class: [
        colorVariants.solid.default,
        "data-[hover=true]:bg-primary-dark data-[hover=true]:text-primary-dark-foreground",
      ],
      color: "default",
      variant: "solid",
    },
    {
      class: [
        colorVariants.solid.primary,
        "data-[hover=true]:bg-primary-dark data-[hover=true]:text-primary-dark-foreground",
      ],
      color: "primary",
      variant: "solid",
    },
    {
      class: [
        colorVariants.solid.success,
        "data-[hover=true]:bg-success-dark data-[hover=true]:text-primary-dark-foreground",
      ],
      color: "success",
      variant: "solid",
    },
    {
      class: [
        colorVariants.solid.warning,
        "data-[hover=true]:bg-warning-dark data-[hover=true]:text-warning-dark-foreground",
      ],
      color: "warning",
      variant: "solid",
    },
    {
      class: [colorVariants.solid.danger, "data-[hover=true]:bg-danger-dark"],
      color: "danger",
      variant: "solid",
    },
    {
      class: ["bg-black", "text-white", "data-[hover=true]:bg-gray-700"],
      color: "black",
      variant: "solid",
    },
    {
      class: [
        "bg-white",
        "text-black",
        "data-[hover=true]:bg-white-100 data-focus-visible:ring-1 data-focus-visible:ring-offset-1 data-focus-visible:ring-white",
      ],
      color: "white",
      variant: "solid",
    },
    // shadow / color
    {
      class: colorVariants.shadow.default,
      color: "default",
      variant: "shadow",
    },
    {
      class: colorVariants.shadow.primary,
      color: "primary",
      variant: "shadow",
    },
    {
      class: colorVariants.shadow.success,
      color: "success",
      variant: "shadow",
    },
    {
      class: colorVariants.shadow.danger,
      color: "danger",
      variant: "shadow",
    },
    // bordered / color
    {
      class: colorVariants.bordered.default,
      color: "default",
      variant: "bordered",
    },
    {
      class: colorVariants.bordered.primary,
      color: "primary",
      variant: "bordered",
    },
    {
      class: colorVariants.bordered.success,
      color: "success",
      variant: "bordered",
    },
    {
      class: colorVariants.bordered.danger,
      color: "danger",
      variant: "bordered",
    },
    // flat / color
    {
      class: colorVariants.flat.default,
      color: "default",
      variant: "flat",
    },
    {
      class: colorVariants.flat.primary,
      color: "primary",
      variant: "flat",
    },
    {
      class: colorVariants.flat.success,
      color: "success",
      variant: "flat",
    },
    {
      class: colorVariants.flat.danger,
      color: "danger",
      variant: "flat",
    },
    // faded / color
    {
      class: [
        colorVariants.faded.default,
        "data-[hover=true]:bg-default-light",
      ],
      color: "default",
      variant: "faded",
    },
    {
      class: [
        colorVariants.faded.primary,
        "data-[hover=true]:bg-primary-light",
      ],
      color: "primary",
      variant: "faded",
    },
    // light / color
    {
      class: [
        colorVariants.light.default,
        "data-[hover=true]:bg-default-light data-[hover=true]:text-default-dark",
      ],
      color: "default",
      variant: "light",
    },
    {
      class: [
        colorVariants.light.primary,
        "data-[hover=true]:bg-primary-light data-[hover=true]:text-primary-dark",
      ],
      color: "primary",
      variant: "light",
    },
    {
      class: [
        colorVariants.light.success,
        "data-[hover=true]:bg-success-light data-[hover=true]:text-success-dark",
      ],
      color: "success",
      variant: "light",
    },
    {
      class: [
        colorVariants.light.warning,
        "text-warning-dark",
        "data-[hover=true]:bg-warning-light data-[hover=true]:text-warning-dark",
      ],
      color: "warning",
      variant: "light",
    },
    {
      class: [
        colorVariants.light.danger,
        "data-[hover=true]:bg-danger-light data-[hover=true]:text-danger-dark",
      ],
      color: "danger",
      variant: "light",
    },
    {
      class: [
        "text-black",
        "data-[hover=true]:bg-black/15",
        "disabled:bg-gray-200",
      ],
      color: "black",
      variant: "light",
    },
    {
      class: [
        "text-white",
        "data-[hover=true]:bg-white/15",
        "disabled:bg-gray-200",
      ],
      color: "white",
      variant: "light",
    },
    // ghost / color
    {
      class: [colorVariants.ghost.default, "data-[hover=true]:bg-default!"],
      color: "default",
      variant: "ghost",
    },
    {
      class: [
        colorVariants.ghost.primary,
        "data-[hover=true]:bg-primary! data-[hover=true]:text-primary-foreground!",
      ],
      color: "primary",
      variant: "ghost",
    },
    {
      class: [
        colorVariants.ghost.success,
        "data-[hover=true]:bg-success! data-[hover=true]:text-success-foreground!",
      ],
      color: "success",
      variant: "ghost",
    },
    {
      class: [
        colorVariants.ghost.warning,
        "text-warning-dark border-warning-dark",
        "data-[hover=true]:bg-warning-dark! data-[hover=true]:text-warning-dark-foreground!",
      ],
      color: "warning",
      variant: "ghost",
    },
    {
      class: [
        colorVariants.ghost.danger,
        "data-[hover=true]:bg-danger! data-[hover=true]:text-danger-foreground!",
      ],
      color: "danger",
      variant: "ghost",
    },
    {
      class: [
        "bg-white text-black border-black",
        "data-[hover=true]:bg-black! data-[hover=true]:text-white!",
      ],
      color: "black",
      variant: "ghost",
    },
    {
      class: "min-w-8 w-8 h-8 [&>svg]:w-4! [&>span>svg]:w-4! p-0!",
      isIconOnly: true,
      size: "xs",
    },
    {
      class: "min-w-8 w-8 h-8 p-0!",
      isIconOnly: true,
      size: "sm",
    },
    {
      class: "min-w-10 w-10 h-10 p-0!",
      isIconOnly: true,
      size: "md",
    },
    {
      class: "min-w-12 w-12 h-12 p-0!",
      isIconOnly: true,
      size: "lg",
    },
    // toggle-bordered / size
    {
      class: [
        "h-8! p-2!",
        "[&>svg]:w-4! [&>svg]:h-4! [&>span>svg]:w-4! [&>span>svg]:h-4!",
      ],
      size: "sm",
      variant: "toggle-bordered",
    },
    {
      class: [
        "h-10! p-[10px]",
        "[&>svg]:w-5! [&>svg]:h-5! [&>span>svg]:w-5! [&>span>svg]:h-5!",
      ],
      size: "md",
      variant: "toggle-bordered",
    },
    {
      class: [
        "h-12! p-3 min-w-12! text-large",
        "[&>svg]:w-6! [&>svg]:h-6! [&>span>svg]:w-6! [&>span>svg]:h-6! ",
      ],
      size: "lg",
      variant: "toggle-bordered",
    },
    // isIconOnly / size
    {
      class: [
        "h-8! min-w-8! w-8! p-0",
        "[&>svg]:w-4! [&>svg]:h-4! [&>span>svg]:w-4! [&>span>svg]:h-4!",
      ],
      isIconOnly: true,
      size: "sm",
      variant: "toggle-bordered",
    },
    {
      class: [
        "h-10! min-w-10! w-10! text-midum p-0 text-primary",
        "[&>svg]:w-5! [&>svg]:h-5! [&>span>svg]:w-5! [&>span>svg]:h-5!",
      ],
      isIconOnly: true,
      size: "md",
      variant: "toggle-bordered",
    },
    {
      class: [
        "h-12! min-w-12! text-large",
        "[&>svg]:w-6! [&>svg]:h-6! [&>span>svg]:w-6! [&>span>svg]:h-6! ",
      ],
      isIconOnly: true,
      size: "lg",
      variant: "toggle-bordered",
    },
    // toggle / size
    {
      class: ["[&>svg]:w-4! [&>svg]:h-4! [&>span>svg]:w-4! [&>span>svg]:h-4!"],
      size: "sm",
      variant: "toggle",
    },
    {
      class: ["[&>svg]:w-5! [&>svg]:h-5! [&>span>svg]:w-5! [&>span>svg]:h-5!"],
      size: "md",
      variant: "toggle",
    },
    {
      class: ["[&>svg]:w-6! [&>svg]:h-6! [&>span>svg]:w-6! [&>span>svg]:h-6!"],
      size: "lg",
      variant: "toggle",
    },
  ],
  defaultVariants: {
    color: "default",
    fullWidth: false,
    isDisabled: false,
    isInGroup: false,
    size: "md",
    variant: "solid",
  },
  variants: {
    color: {
      black: "data-[disabled=true]:text-default-disabled",
      danger:
        "opacity-100! data-[disabled=true]:text-default-disabled data-[hover=true]:opacity-100!",
      default:
        "opacity-100! data-[disabled=true]:text-default-disabled data-[hover=true]:opacity-100!",
      primary:
        "opacity-100! data-[disabled=true]:text-default-disabled data-[hover=true]:opacity-100!",
      success:
        "opacity-100! data-[disabled=true]:text-default-disabled data-[hover=true]:opacity-100!",
      warning:
        "opacity-100! data-[disabled=true]:text-default-disabled data-[hover=true]:opacity-100!",
      white: "data-[disabled=true]:text-default-disabled border-gray-200",
    },
    disableAnimation: {
      false:
        "transition-transform-colors-opacity motion-reduce:transition-none",
      true: "transition-none! data-[pressed=true]:scale-100",
    },
    fullWidth: {
      true: "w-full",
    },
    isDisabled: {
      true: "pointer-events-none",
    },
    isIconOnly: {
      false: "",
      true: "px-0 gap-0!",
    },
    isInGroup: {
      true: "[&:not(:first-child):not(:last-child)]:rounded-none",
    },
    size: {
      lg: "font-medium px-3 py-4 min-w-24 h-12 text-medium gap-3 rounded-small [&>svg]:w-5! [&>span>svg]:w-5! [&>svg]:stroke-lg!",
      md: "font-medium px-3 py-4 min-w-20 h-10 text-small gap-3 rounded-small [&>svg]:w-4! [&>span>svg]:w-4! [&>svg]:stroke-md!",
      sm: "font-medium px-3 py-3 min-w-16 h-8 text-small gap-2 rounded-small [&>svg]:w-4! [&>span>svg]:w-4! [&>svg]:stroke-sm!",
      xs: "font-medium px-3 py-3 min-w-16 h-8 text-tiny gap-2 rounded-small [&>svg]:w-4! [&>span>svg]:w-4! [&>svg]:stroke-sm!",
    },
    variant: {
      bordered:
        "border-small bg-transparent data-[disabled=true]:bg-gray-200 data-[hover=true]:no-underline",
      faded:
        "data-[disabled=true]:bg-gray-200 data-[disabled=true]:border-gray-300 border-small data-[hover=true]:no-underline",
      ghost:
        "data-[disabled=true]:bg-gray-200 data-[disabled=true]:border-gray-300 border-small bg-transparent data-[hover=true]:no-underline",
      light:
        "font-semibold! gap-2! bg-transparent data-[disabled=true]:bg-gray-200 data-[hover=true]:no-underline",
      solid: "data-[disabled=true]:bg-gray-200 data-[hover=true]:no-underline",
      toggle: `
      p-0 text-primary
      h-full! min-w-10! rounded-none!
      data-[disabled=true]:bg-gray-200
      data-[disabled=true]:text-gray-600
      data-[disabled=true]:data-[selected=true]:bg-gray-200
      data-[disabled=true]:data-[selected=true]:text-gray-600
      data-[selected=true]:data-[disabled=true]:[&_svg]:fill-gray-600
      data-[selected=true]:[&_svg]:fill-primary
      data-[hover=true]:[&_svg]:fill-primary-dark
      data-[hover=true]:[&_svg]:text-primary-dark
      `,

      "toggle-bordered": `
      border border-primary rounded-md text-primary hover:bg-blue-50
      data-[selected=true]:bg-primary-light
      data-[selected=true]:text-primary
      data-[disabled=true]:bg-gray-200
      data-[disabled=true]:text-gray-600
      data-[disabled=true]:border-transparent
      data-[disabled=true]:data-[selected=true]:bg-gray-200
      data-[disabled=true]:data-[selected=true]:text-gray-600
      data-[selected=true]:data-[disabled=true]:[&_svg]:fill-gray-600
      data-[selected=true]:[&_svg]:fill-primary
      `,
    },
  },
});

type ExtendedButtonProps = React.ComponentProps<typeof ExtendedButton>;

export type ButtonVariant = Extract<
  ExtendedButtonProps["variant"],
  "ghost" | "faded" | "solid" | "light" | "toggle" | "toggle-bordered"
>;

type Color = Extract<
  ExtendedButtonProps["color"],
  "primary" | "success" | "warning" | "danger" | "black" | "white"
>;

type BaseButtonProps = Omit<
  ExtendedButtonProps,
  "variant" | "color" | "id" | "onClick" | "disabled"
> & {
  color?: Color;
  "data-testid"?: string;
  id: string;
  tour?: TourElement;
  onClick?: NextUIButtonProps["onClick"]; // @deprecated please use onPress instead
  variant?: ButtonVariant;
  trackEventProps?: TrackEventProps;
  ref?: React.Ref<HTMLButtonElement>;
};

export type IconOnlyButtonProps = BaseButtonProps & {
  isIconOnly: true;
  "aria-label": string;
};

export type NonIconOnlyButtonProps = BaseButtonProps & {
  isIconOnly?: false | undefined;
};

export type ButtonProps = IconOnlyButtonProps | NonIconOnlyButtonProps;

/**
 * Extracts and removes size and className props from icon elements.
 * The size prop is removed to prevent inline styles from overriding button CSS classes.
 * The className is extracted and moved to the span wrapper for proper layout control.
 */
function extractIconProps(element: React.ReactNode): {
  className?: string;
  element: React.ReactNode;
} {
  if (!React.isValidElement(element)) {
    return { element };
  }

  const props = element.props;
  if (!props || typeof props !== "object") {
    return { element };
  }

  const propsRecord = props as Record<string, unknown>;
  const hasSize = "size" in propsRecord;
  const className = propsRecord.className as string | undefined;

  if (!hasSize && !className) {
    return { element };
  }

  const { className: _className, size: _size, ...restProps } = propsRecord;
  const cleanedElement = React.cloneElement(element, restProps);
  return {
    className,
    element: cleanedElement,
  };
}

function processIconContent(
  isLoading: boolean | undefined,
  content: React.ReactNode | undefined,
): React.ReactNode {
  if (isLoading || !content) {
    return undefined;
  }
  const { className, element } = extractIconProps(content);
  return className ? (
    <span className={className}>{element}</span>
  ) : (
    <span>{element}</span>
  );
}

export function Button({ ref, tour, ...props }: ButtonProps) {
  const { trackEvent } = useTracking();
  const translations = useTranslations();

  const processedStartContent = useMemo(
    () => processIconContent(props.isLoading, props.startContent),
    [props.isLoading, props.startContent],
  );

  const processedEndContent = useMemo(
    () => processIconContent(props.isLoading, props.endContent),
    [props.isLoading, props.endContent],
  );

  const spinnerElement = useMemo(
    () => (
      <Spinner
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuetext={translations.general.loading}
        role="progressbar"
        size={props.size !== "lg" ? "xs" : "sm"}
        testId={props.id}
      />
    ),
    [props.size, props.id],
  );

  return (
    <ExtendedButton
      ref={ref}
      {...props}
      {...tour}
      as={props.as ?? "button"}
      color={props.color ?? "primary"}
      data-testid={props["data-testid"] ?? props.id}
      endContent={processedEndContent}
      role={props.as === "a" ? "link" : "button"}
      spinner={spinnerElement}
      startContent={processedStartContent}
      onPress={(e: PressEvent) => {
        trackEvent({
          ...props.trackEventProps,
          name: generateTrackName({
            id: props.id,
            trackEventProps: props.trackEventProps,
          }),
        });
        props.onPress?.(e);
      }}
    />
  );
}

export const BUTTON_DISPLAY_NAME = "RecareUI.Button";
Button.displayName = BUTTON_DISPLAY_NAME;
